import { useEffect, useState } from "react";

import { usePermanentRealTimeUser } from "hooks";
import { setStarIndicatorsToFilter, useStarIndicatorsToFilter } from "modules/Appointments/hooks";
import { APPOINTMENTS_STORAGE_KEYS } from "modules/Appointments/storageKeys";
import { indicatorsFiltrationLogic, initializeIdentifiers } from "modules/Appointments/util";

import type { IndicatorFilters } from "modules/Appointments/components";
import type { StarIndicatorType } from "modules/Appointments/queryKeys";

export enum STAR_FILTERS {
  Star = "is_star",
  Green = "is_star_green",
  Blue = "is_star_blue",
  Yellow = "is_star_yellow",
  Red = "is_star_red"
}

const getStarIndicator = (isActive: boolean, filter: string, borderColor?: string) => ({ icon: "is_star", active: isActive, filter, borderColor });

const useGetStarOptions = (specialIndicatorsData?: StarIndicatorType): IndicatorFilters[] => {
  const { user } = usePermanentRealTimeUser();
  const isStarEnabled = user?.star_color_red_enabled || user?.star_color_blue_enabled || user?.star_color_yellow_enabled || user?.star_color_green_enabled;

  return [
    ...(!isStarEnabled ? [getStarIndicator(Boolean(specialIndicatorsData?.is_star), STAR_FILTERS.Star)] : []),
    ...(user?.star_color_green_enabled ? [getStarIndicator(Boolean(specialIndicatorsData?.is_star_green), STAR_FILTERS.Green, "green")] : []),
    ...(user?.star_color_blue_enabled ? [getStarIndicator(Boolean(specialIndicatorsData?.is_star_blue), STAR_FILTERS.Blue, "blue")] : []),
    ...(user?.star_color_yellow_enabled ? [getStarIndicator(Boolean(specialIndicatorsData?.is_star_yellow), STAR_FILTERS.Yellow, "yellow")] : []),
    ...(user?.star_color_red_enabled ? [getStarIndicator(Boolean(specialIndicatorsData?.is_star_red), STAR_FILTERS.Red, "red")] : [])
  ];
};

export const useGetStarIndicators = () => {
  const specialIndicatorsData = useStarIndicatorsToFilter();
  const starIndicators = useGetStarOptions(specialIndicatorsData);

  const [starFilters, setStarFilters] = useState<IndicatorFilters[]>(initializeIdentifiers(starIndicators, APPOINTMENTS_STORAGE_KEYS.StarIndicatorPreference));

  useEffect(() => {
    const updateIndicatorsQueryData = () => {
      const indicatorFilters = starFilters.filter(({ active, filter }) => active && filter).reduce((acc, item) => ({ ...acc, [item.filter]: item.filter }), {});

      if (JSON.stringify(specialIndicatorsData) !== JSON.stringify(indicatorFilters)) setStarIndicatorsToFilter(indicatorFilters);
    };

    updateIndicatorsQueryData();
  }, [starFilters]);

  const updateStarFilter = (filterIndex: number) =>
    setStarFilters(current => indicatorsFiltrationLogic(current, filterIndex, APPOINTMENTS_STORAGE_KEYS.StarIndicatorPreference));

  return { starFilters, updateStarFilter };
};
